import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { callWithMisdirect } from "@/components/bank/MisdirectPrompt";
import { BANK_MATCH_STATUS } from "@/lib/tickets";
import { formatEUR, formatDate, POSTING_CATEGORIES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, Bank, CheckCircle, XCircle, Link as LinkIcon, PencilSimple, MagnifyingGlass, Trash, ArrowClockwise, Sparkle, ArrowsClockwise, DotsThreeVertical, Scissors } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useSearchParams } from "react-router-dom";

export default function BankPage() {
  const { user } = useAuth();
  const canDelete = ["super_admin", "admin", "manager"].includes(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const jumpTxId = searchParams.get("tx");
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("booking_date");
  const [sortDir, setSortDir] = useState("desc");
  const [open, setOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [reassigning, setReassigning] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // iter164 — Buchung in zwei aufteilen
  const [splitting, setSplitting] = useState(null);
  const [splitPart1, setSplitPart1] = useState("");
  const [tenancies, setTenancies] = useState([]);
  // 2026-07 — Echte Gesamt-Statistik aus DB, nicht aus geladener Seite (die max. 500 zeigt)
  const [stats, setStats] = useState({ total: 0, auto: 0, manual: 0, unmatched: 0, sum: 0 });

  const loadStats = async () => {
    try {
      const { data } = await api.get("/bank/transactions/stats");
      setStats(data);
    } catch { /* stats sind best-effort */ }
  };

  const [direction, setDirection] = useState("all"); // 2026-03 — Eingang/Ausgang-Filter
  // 2026-03 — Zeitraum-Filter (Presets + Freie Eingabe). Wirkt sowohl auf
  // die geladene Liste als auch auf „Buchungen neu prüfen".
  const [periodPreset, setPeriodPreset] = useState("all"); // all | q1_2026 | q2_2026 | year_2026 | year_2025 | last_3m | last_6m | ytd | current_month | custom
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const _computePeriodRange = (preset) => {
    const now = new Date();
    const y = now.getFullYear();
    const iso = (d) => d.toISOString().slice(0, 10);
    switch (preset) {
      case "current_month":
        return { from: iso(new Date(y, now.getMonth(), 1)),
                 to: iso(new Date(y, now.getMonth() + 1, 0)) };
      case "last_3m": {
        const from = new Date(y, now.getMonth() - 2, 1);
        return { from: iso(from), to: iso(now) };
      }
      case "last_6m": {
        const from = new Date(y, now.getMonth() - 5, 1);
        return { from: iso(from), to: iso(now) };
      }
      case "ytd":
        return { from: `${y}-01-01`, to: iso(now) };
      case "q1_2026": return { from: "2026-01-01", to: "2026-03-31" };
      case "q2_2026": return { from: "2026-04-01", to: "2026-06-30" };
      case "q3_2026": return { from: "2026-07-01", to: "2026-09-30" };
      case "q4_2026": return { from: "2026-10-01", to: "2026-12-31" };
      case "year_2026": return { from: "2026-01-01", to: "2026-12-31" };
      case "year_2025": return { from: "2025-01-01", to: "2025-12-31" };
      case "year_2024": return { from: "2024-01-01", to: "2024-12-31" };
      case "custom":
        return { from: customFrom || null, to: customTo || null };
      case "all":
      default:
        return { from: null, to: null };
    }
  };

  const _periodParams = () => {
    const { from, to } = _computePeriodRange(periodPreset);
    const p = {};
    if (from) p.date_from = from;
    if (to) p.date_to = to;
    return p;
  };

  const _periodLabel = () => {
    const map = {
      all: "Gesamter Zeitraum", current_month: "Aktueller Monat",
      last_3m: "Letzte 3 Monate", last_6m: "Letzte 6 Monate",
      ytd: "Bisheriges Jahr", q1_2026: "Q1 2026", q2_2026: "Q2 2026",
      q3_2026: "Q3 2026", q4_2026: "Q4 2026",
      year_2026: "2026", year_2025: "2025", year_2024: "2024",
      custom: customFrom || customTo ? `${customFrom || "…"} – ${customTo || "…"}` : "Frei",
    };
    return map[periodPreset] || "";
  };

  const load = async () => {
    const params = { sort_by: sortBy, sort_dir: sortDir };
    if (filter !== "all") params.status = filter;
    if (direction === "in") params.amount_min = 0.01;
    else if (direction === "out") params.amount_max = -0.01;
    if (search) params.search = search;
    Object.assign(params, _periodParams());
    const { data } = await api.get("/bank/transactions", { params });
    setItems(data);
    // Stats gleich mitziehen, damit Kacheln immer aktuelle DB-Zahl zeigen
    loadStats();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, direction, sortBy, sortDir, periodPreset, customFrom, customTo]);
  // Debounce search
  useEffect(() => {
    const h = setTimeout(load, 350);
    return () => clearTimeout(h);
    /* eslint-disable-next-line */
  }, [search]);
  useEffect(() => { api.get("/tenancies").then((r) => setTenancies(r.data)); }, []);

  // 2026-02 — Sprung „Zur Bankbuchung" aus dem Mietkonto:
  // Wenn ?tx=<id> in der URL → Filter zurücksetzen, alle Buchungen laden und
  // die Zeile scrollen + markieren.
  useEffect(() => {
    if (!jumpTxId) return;
    // Filter erweitern, damit die Ziel-Buchung sicher im Datensatz ist
    setFilter("all");
    setSearch("");
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="bank-row-${jumpTxId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-teal-500", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-teal-500", "ring-offset-2"), 3500);
        // Query-Param entfernen, damit späterer Refresh nicht erneut springt
        setSearchParams({}, { replace: true });
      }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpTxId, items.length]);

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      const { data } = await api.delete(`/bank/transactions/${deleteTarget.id}`);
      toast.success(`Buchung gelöscht. ${data.postings_deleted || 0} Ist-Buchung(en) mit entfernt.`);
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Löschen"); }
  };

  const doReassign = async (tenancyId) => {
    if (!reassigning) return;
    try {
      const data = await callWithMisdirect(async (extra) => (await api.patch(`/bank/transactions/${reassigning.id}/reassign`, {
        tenancy_id: tenancyId, ...extra,
      })).data);
      if (!data) return;
      toast.success(`Neu zugeordnet. ${data.posting_ids?.length || 0} Ist-Buchung(en) erzeugt.`);
      setReassigning(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler bei Neu-Zuordnung"); }
  };

  const [rematching, setRematching] = useState(false);
  // 2026-02 — Mehrfach-Auswahl im Bank-Modul
  const [selectedTx, setSelectedTx] = useState(new Set());
  const toggleSelectTx = (txId) => {
    setSelectedTx((prev) => {
      const n = new Set(prev);
      if (n.has(txId)) n.delete(txId); else n.add(txId);
      return n;
    });
  };
  const clearTxSelection = () => setSelectedTx(new Set());
  const selectAllVisibleTx = () => setSelectedTx(new Set(items.map((t) => t.id)));

  const bulkUnlinkTx = async () => {
    const linked = items.filter((t) => selectedTx.has(t.id) && t.matched_tenancy_id);
    if (!linked.length) {
      toast.info("Keine der gewählten Buchungen ist einem Mietkonto zugeordnet.");
      return;
    }
    if (!await window._domivusConfirm(
      `${linked.length} Verknüpfung(en) aufheben? Alle zugehörigen Ist-Buchungen (inkl. Split-Gruppen) werden aus dem Mietkonto entfernt und die Bank-Zeilen erscheinen wieder als „unzugeordnet".`
    )) return;
    try {
      const { data } = await api.post("/bank/transactions/bulk/unlink", { ids: linked.map((t) => t.id) });
      toast.success(`${data.unlinked} Buchung(en) freigegeben · ${data.postings_removed} Ist-Buchung(en) entfernt${data.split_groups_removed ? ` · ${data.split_groups_removed} Split-Gruppe(n)` : ""}`);
      clearTxSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aufheben fehlgeschlagen");
    }
  };

  const bulkDeleteTx = async () => {
    const ids = Array.from(selectedTx);
    if (!ids.length) return;
    if (!await window._domivusConfirm(
      `${ids.length} Bankbuchung(en) löschen? Zugehörige Ist-Buchungen im Mietkonto werden ebenfalls entfernt und die Bank-Zeilen archiviert.`
    )) return;
    try {
      const { data } = await api.post("/bank/transactions/bulk/delete", { ids });
      toast.success(`${data.deleted} Bank-Buchung(en) gelöscht · ${data.postings_removed} Ist-Buchung(en) entfernt`);
      clearTxSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const bulkIgnoreTx = async () => {
    const ids = Array.from(selectedTx);
    if (!ids.length) return;
    if (!await window._domivusConfirm(`${ids.length} Buchung(en) als „ignoriert" markieren?`)) return;
    try {
      const { data } = await api.post("/bank/transactions/bulk/ignore", { ids });
      toast.success(`${data.ignored} Buchung(en) ignoriert`);
      clearTxSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ignorieren fehlgeschlagen");
    }
  };
  const doRematch = async () => {
    if (rematching) return;
    setRematching(true);
    try {
      const { from, to } = _computePeriodRange(periodPreset);
      const params = { include_all: true };
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const { data } = await api.post("/bank/rematch-unmatched", null, { params });
      const matched = data?.matched || data?.matched_now || 0;
      const suggestions = data?.with_suggestions || data?.suggestions || 0;
      const rechecked = data?.rechecked || 0;
      const scope = periodPreset === "all" ? "" : ` (Zeitraum: ${_periodLabel()})`;
      toast.success(
        `Bankbuchungen neu geprüft${scope}: ${rechecked} · ${matched} neu automatisch zugeordnet` +
        (suggestions ? ` · ${suggestions} Vorschläge im Bank-Assistenten` : "")
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler bei der Neu-Prüfung");
    } finally {
      setRematching(false);
    }
  };

  // 2026-03 — Kompletter Reset: alle Verknüpfungen aufheben + Auto-Match neu.
  const doUnlinkAllAndRematch = async () => {
    if (rematching) return;
    const scopeText = periodPreset === "all" ? "" : ` (Zeitraum: ${_periodLabel()})`;
    if (!window.confirm(
      `Wirklich ALLE Verknüpfungen${scopeText} aufheben und danach automatisch neu zuordnen?\n\n` +
      "• Alle Ist-Buchungen aus dem Mietkonto, die aus Bankimport stammen, werden gelöscht.\n" +
      "• Alle Bank-Buchungen werden anschließend erneut mit der aktuellen Matching-Logik geprüft.\n" +
      "• Manuelle Sollstellungen und selbst erfasste Ist-Buchungen bleiben unberührt."
    )) return;
    setRematching(true);
    try {
      const u = await api.post("/bank/transactions/unlink-all");
      const uData = u.data || {};
      toast.info(
        `${uData.unlinked || 0} Verknüpfungen aufgehoben` +
        (uData.postings_removed ? ` · ${uData.postings_removed} Ist-Postings entfernt` : "") +
        (uData.split_groups_removed ? ` · ${uData.split_groups_removed} Split-Gruppen entfernt` : "")
      );
      const { from, to } = _computePeriodRange(periodPreset);
      const params = { include_all: true };
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const r = await api.post("/bank/rematch-unmatched", null, { params });
      const rData = r.data || {};
      toast.success(
        `Reset abgeschlossen${scopeText} · ${rData.matched || 0} automatisch zugeordnet · ` +
        `${rData.rechecked || 0} Buchungen neu geprüft`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset fehlgeschlagen");
    } finally {
      setRematching(false);
    }
  };

  // Alter Reduce entfernt — stats kommt jetzt aus DB via loadStats().
  // Für die "sum" Anzeige ist der DB-Wert präziser als die geladene Client-Seite.

  return (
    <div className="space-y-4" data-testid="bank-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Bankbuchungen</h1>
          <p className="text-sm text-slate-500 mt-1">Bankimport mit automatischer Zahlungszuordnung</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" className="h-9" data-testid="bank-payout-orders-link">
            <Link to="/zahlungsauftraege">Zahlungsaufträge</Link>
          </Button>
          <Button
            variant="outline"
            className="gap-2 h-9 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
            data-testid="bank-rematch-btn"
            onClick={doRematch}
            disabled={rematching}
          >
            <ArrowsClockwise size={16} className={rematching ? "animate-spin" : ""} />
            {rematching ? "Prüfe…" : "Buchungen neu prüfen"}
          </Button>
          {/* 2026-03 — Zerstörerische Aktionen bewusst im Overflow-Menü versteckt */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9"
                      data-testid="bank-overflow-menu" title="Weitere Aktionen">
                <DotsThreeVertical size={16} weight="bold" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-[10px] uppercase text-slate-500">Wartungs-Aktionen</DropdownMenuLabel>
              <DropdownMenuItem onClick={doUnlinkAllAndRematch} disabled={rematching}
                                data-testid="bank-reset-btn"
                                className="text-rose-800 focus:text-rose-900 focus:bg-rose-50 cursor-pointer">
                <ArrowsClockwise size={14} className={`mr-2 ${rematching ? "animate-spin" : ""}`} />
                <div className="flex flex-col">
                  <span>Reset & Neu-Zuordnen</span>
                  <span className="text-[10px] text-slate-500">Alle Verknüpfungen aufheben + Auto-Match</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/bank/assistent">
            <Button variant="outline" className="gap-2 h-9 border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="bank-assistent-btn">
              <Sparkle size={16} weight="fill" /> Bank-Assistent
            </Button>
          </Link>
          <Link to="/archiv">
            <Button variant="outline" className="gap-2 h-9" data-testid="bank-archiv-btn">
              <ArrowClockwise size={16} /> Archiv
            </Button>
          </Link>
          <Dialog open={manualOpen} onOpenChange={setManualOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2 h-9" data-testid="bank-manual-btn">
                <PencilSimple size={14} /> Manueller Eintrag
              </Button>
            </DialogTrigger>
            <ManualEntryDialog onSaved={() => { setManualOpen(false); load(); }} />
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="bank-import-btn">
                <Upload size={16} /> Import (CSV / CAMT.053 / MT940)
              </Button>
            </DialogTrigger>
            <ImportDialog onSaved={() => { setOpen(false); load(); }} />
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Buchungen gesamt" value={stats.total} />
        <Stat label="Automatisch zugeordnet" value={stats.auto || 0} accent="text-emerald-700" />
        <Stat label="Manuell zugeordnet" value={stats.manual || 0} accent="text-blue-700" />
        <Stat label="Offen / Klärung" value={stats.unmatched || 0} accent={stats.unmatched ? "text-amber-700" : "text-slate-700"} />
      </div>

      {stats.total > items.length && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded p-2.5 flex items-center gap-2" data-testid="bank-pagination-hint">
          <span>⚠ Es werden aktuell die neuesten {items.length} von {stats.total} Buchungen angezeigt. Nutzen Sie Filter/Suche, um ältere Buchungen zu finden.</span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-2 flex-wrap" data-testid="bank-period-toolbar">
        <Label className="text-xs text-slate-600 mr-1">Zeitraum:</Label>
        {[
          { k: "all", l: "Alle" },
          { k: "current_month", l: "Aktueller Monat" },
          { k: "last_3m", l: "Letzte 3 Mon." },
          { k: "last_6m", l: "Letzte 6 Mon." },
          { k: "ytd", l: "YTD" },
          { k: "q1_2026", l: "Q1 2026" },
          { k: "q2_2026", l: "Q2 2026" },
          { k: "q3_2026", l: "Q3 2026" },
          { k: "q4_2026", l: "Q4 2026" },
          { k: "year_2026", l: "2026" },
          { k: "year_2025", l: "2025" },
          { k: "year_2024", l: "2024" },
        ].map((p) => (
          <button
            key={p.k}
            onClick={() => setPeriodPreset(p.k)}
            className={`px-2.5 py-1 rounded text-xs border transition ${
              periodPreset === p.k
                ? "bg-teal-600 text-white border-teal-600 shadow-sm"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
            data-testid={`bank-period-${p.k}`}
          >
            {p.l}
          </button>
        ))}
        <button
          onClick={() => setPeriodPreset("custom")}
          className={`px-2.5 py-1 rounded text-xs border transition ${
            periodPreset === "custom"
              ? "bg-teal-600 text-white border-teal-600 shadow-sm"
              : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
          }`}
          data-testid="bank-period-custom"
        >
          Frei
        </button>
        {periodPreset === "custom" && (
          <div className="flex items-center gap-1.5 ml-1">
            <Input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="h-7 text-xs w-36"
              data-testid="bank-period-custom-from"
            />
            <span className="text-slate-400 text-xs">–</span>
            <Input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="h-7 text-xs w-36"
              data-testid="bank-period-custom-to"
            />
          </div>
        )}
        {periodPreset !== "all" && (
          <span className="text-[11px] text-slate-500 ml-1" data-testid="bank-period-hint">
            „Neu prüfen" wirkt nur auf: <b>{_periodLabel()}</b>
          </span>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-3 flex-wrap">
        <Label className="text-xs text-slate-600">Status:</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44 h-9" data-testid="bank-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            {Object.entries(BANK_MATCH_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Label className="text-xs text-slate-600">Richtung:</Label>
        <Select value={direction} onValueChange={setDirection}>
          <SelectTrigger className="w-36 h-9" data-testid="bank-direction-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="in">Nur Eingänge</SelectItem>
            <SelectItem value="out">Nur Ausgänge</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[220px] max-w-[420px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-9 pl-8 text-sm"
            placeholder="Suche in Verwendungszweck, Name, IBAN…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="bank-search-input"
          />
        </div>
        <Label className="text-xs text-slate-600 ml-auto">Sortierung:</Label>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-40 h-9" data-testid="bank-sort-by"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="booking_date">Datum</SelectItem>
            <SelectItem value="amount">Betrag</SelectItem>
            <SelectItem value="counterparty_name">Auftraggeber</SelectItem>
            <SelectItem value="match_status">Status</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline" size="sm" className="h-9 gap-1"
          onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
          data-testid="bank-sort-dir"
          title={sortDir === "asc" ? "Aufsteigend" : "Absteigend"}
        >
          {sortDir === "asc" ? "↑" : "↓"}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <Th className="w-9 pl-3">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  checked={items.length > 0 && selectedTx.size === items.length}
                  ref={(el) => {
                    if (el) el.indeterminate = selectedTx.size > 0 && selectedTx.size < items.length;
                  }}
                  onChange={(e) => e.target.checked ? selectAllVisibleTx() : clearTxSelection()}
                  data-testid="bank-select-all"
                  title="Alle sichtbaren auswählen"
                />
              </Th>
              <SortTh k="booking_date" cur={sortBy} dir={sortDir} setBy={setSortBy} setDir={setSortDir}>Datum</SortTh>
              <SortTh k="counterparty_name" cur={sortBy} dir={sortDir} setBy={setSortBy} setDir={setSortDir}>Auftraggeber</SortTh>
              <SortTh k="purpose" cur={sortBy} dir={sortDir} setBy={setSortBy} setDir={setSortDir}>Verwendungszweck</SortTh>
              <SortTh k="amount" cur={sortBy} dir={sortDir} setBy={setSortBy} setDir={setSortDir} className="text-right">Betrag</SortTh>
              <SortTh k="match_status" cur={sortBy} dir={sortDir} setBy={setSortBy} setDir={setSortDir}>Status</SortTh>
              <SortTh k="contract_number" cur={sortBy} dir={sortDir} setBy={setSortBy} setDir={setSortDir}>Zuordnung</SortTh>
              <Th className="w-32 text-right"></Th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-slate-500">
                <Bank size={32} className="mx-auto mb-2 text-slate-300" />
                Noch keine Bankbuchungen importiert.
              </td></tr>
            )}
            {items.map((t) => {
              const s = BANK_MATCH_STATUS[t.match_status] || BANK_MATCH_STATUS.unmatched;
              return (
                <tr key={t.id} className={`border-t border-slate-100 hover:bg-slate-50 ${selectedTx.has(t.id) ? "bg-teal-50/50" : ""}`} data-testid={`bank-row-${t.id}`}>
                  <Td className="pl-3">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                      checked={selectedTx.has(t.id)}
                      onChange={() => toggleSelectTx(t.id)}
                      data-testid={`bank-select-${t.id}`}
                    />
                  </Td>
                  <Td className="tabular-nums whitespace-nowrap">{formatDate(t.booking_date)}</Td>
                  <Td className="max-w-[180px] truncate" title={t.counterparty_name}>{t.counterparty_name || "—"}</Td>
                  <Td className="max-w-[280px] text-xs text-slate-600 truncate" title={t.purpose}>{t.purpose || "—"}</Td>
                  <Td className={`text-right tabular-nums font-medium ${t.amount > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {formatEUR(t.amount)}
                  </Td>
                  <Td>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.className}`}>{s.label}</span>
                    {(t.match_reason === "learned_rule" || t._match_reason === "learned_rule") && (
                      <span className="ml-1 inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700"
                            title="Automatisch zugeordnet auf Basis einer gelernten Regel"
                            data-testid={`bank-learn-chip-${t.id}`}>
                        🧠 gelernt
                      </span>
                    )}
                  </Td>
                  <Td className="font-mono text-xs">{t.contract_number || "—"}</Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1">
                      {t.match_status === "unmatched" && (
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                          onClick={() => setAssigning(t)} data-testid={`bank-assign-${t.id}`}>
                          <LinkIcon size={12} /> Zuordnen
                        </Button>
                      )}
                      {t.match_status === "unmatched" && Math.abs(t.amount || 0) > 0.02 && (
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:text-teal-700"
                          onClick={() => setSplitting(t)} data-testid={`bank-split-${t.id}`}
                          title="Buchung in zwei aufteilen (z. B. Voreigentümer hat 2 Mieten zusammengefasst überwiesen)">
                          <Scissors size={12} />
                        </Button>
                      )}
                      {t.matched_tenancy_id && (
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                          onClick={() => setReassigning(t)} data-testid={`bank-reassign-${t.id}`}
                          title="Zuordnung ändern (z. B. falscher Auto-Match)">
                          <ArrowClockwise size={12} /> Umbuchen
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:text-rose-700"
                          onClick={() => setDeleteTarget(t)} data-testid={`bank-delete-${t.id}`}
                          title="Buchung löschen (inkl. Ist-Postings)">
                          <Trash size={12} />
                        </Button>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedTx.size > 0 && (
        <div
          className="sticky bottom-3 z-30 mx-auto max-w-3xl flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900 text-slate-50 px-4 py-2.5 shadow-lg"
          data-testid="bank-bulk-bar"
        >
          <div className="text-sm">
            <span className="font-semibold">{selectedTx.size}</span> Buchung{selectedTx.size === 1 ? "" : "en"} ausgewählt
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5 bg-transparent border-slate-500 text-slate-100 hover:bg-slate-800"
              onClick={bulkUnlinkTx}
              data-testid="bank-bulk-unlink"
              title="Verknüpfung zum Mietkonto aufheben (inkl. Split-Gruppen)"
            >
              Verknüpfung aufheben
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5 bg-transparent border-slate-500 text-slate-100 hover:bg-slate-800"
              onClick={bulkIgnoreTx}
              data-testid="bank-bulk-ignore"
            >
              Ignorieren
            </Button>
            {canDelete && (
              <Button
                size="sm"
                className="h-8 gap-1.5 bg-rose-700 hover:bg-rose-800 text-white"
                onClick={bulkDeleteTx}
                data-testid="bank-bulk-delete"
              >
                Löschen
              </Button>
            )}
            <button
              type="button"
              onClick={clearTxSelection}
              className="text-xs text-slate-400 hover:text-slate-100 px-1"
              data-testid="bank-bulk-clear"
              title="Auswahl aufheben"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {assigning && (
        <AssignDialog tx={assigning} tenancies={tenancies} onClose={() => setAssigning(null)} onSaved={() => { setAssigning(null); load(); }} />
      )}

      {/* iter159w — Gelernte Zuordnungen verwalten */}
      <LearnRulesCard />
      {reassigning && (
        <Dialog open onOpenChange={(o) => !o && setReassigning(null)}>
          <DialogContent className="max-w-lg" data-testid="bank-reassign-dialog">
            <DialogHeader>
              <DialogTitle>Zuordnung ändern</DialogTitle>
              <DialogDescription>
                Buchung vom {formatDate(reassigning.booking_date)} · {formatEUR(reassigning.amount)} · {reassigning.counterparty_name || "—"}
                <br />
                Bisher: <span className="font-mono text-xs">{reassigning.contract_number || "—"}</span>. Wähle das neue Mietverhältnis:
              </DialogDescription>
            </DialogHeader>
            {/* 2026-02 — Suchbares MV-Feld statt einfaches Dropdown (auch
                inaktive/beendete MVs anzeigen, da man ggf. auf eine ältere
                Zuordnung umbuchen möchte). */}
            <TenancySearchSelect
              tenancies={tenancies}
              value={null}
              onChange={(id) => doReassign(id)}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setReassigning(null)}>Abbrechen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {deleteTarget && (
        <Dialog open onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <DialogContent data-testid="bank-delete-dialog">
            <DialogHeader>
              <DialogTitle>Bankbuchung löschen?</DialogTitle>
              <DialogDescription>
                Die Buchung vom <b>{formatDate(deleteTarget.booking_date)}</b> über <b>{formatEUR(deleteTarget.amount)}</b> ({deleteTarget.counterparty_name || "—"}) wird entfernt.
                <br />Zugehörige Ist-Buchungen im Mietkonto werden ebenfalls gelöscht (Saldo wird neu berechnet).
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
              <Button className="bg-rose-700 hover:bg-rose-800" onClick={doDelete} data-testid="bank-delete-confirm-btn">
                <Trash size={14} className="mr-1.5" /> Endgültig löschen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* iter164 — Split-Dialog: Bank-Buchung in zwei aufteilen */}
      {splitting && (
        <Dialog open={!!splitting} onOpenChange={(o) => { if (!o) { setSplitting(null); setSplitPart1(""); } }}>
          <DialogContent className="max-w-md" data-testid="bank-split-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Scissors size={18} className="text-teal-700" /> Buchung aufteilen
              </DialogTitle>
              <DialogDescription>
                Zerlegt die Bank-Buchung in ZWEI unabhängige Buchungen (z. B. Voreigentümer
                hat Mieten mehrerer Mieter zusammengefasst überwiesen). Beide Teile lassen
                sich anschließend einzeln zuordnen.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                <div className="text-[10px] uppercase text-slate-500 font-semibold">Original</div>
                <div className="mt-1 font-semibold tabular-nums">
                  {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(splitting.amount || 0)}
                </div>
                <div className="text-xs text-slate-600 mt-1 truncate" title={splitting.purpose}>{splitting.purpose || "—"}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Betrag Teil 1</label>
                <Input
                  type="number" step="0.01" placeholder="z. B. 550,00"
                  value={splitPart1}
                  onChange={(e) => setSplitPart1(e.target.value)}
                  data-testid="bank-split-part1"
                />
                {splitPart1 && Math.abs(parseFloat(splitPart1) || 0) > 0 && Math.abs(parseFloat(splitPart1) || 0) < Math.abs(splitting.amount || 0) && (
                  <div className="text-[11px] text-slate-500 mt-1">
                    Rest (Teil 2): <span className="font-medium">
                      {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Math.abs(splitting.amount || 0) - Math.abs(parseFloat(splitPart1) || 0))}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { setSplitting(null); setSplitPart1(""); }}>Abbrechen</Button>
              <Button
                className="bg-teal-700 hover:bg-teal-800"
                data-testid="bank-split-submit"
                disabled={!splitPart1 || Math.abs(parseFloat(splitPart1) || 0) <= 0 || Math.abs(parseFloat(splitPart1) || 0) >= Math.abs(splitting.amount || 0)}
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/bank/transactions/${splitting.id}/split`, {
                      part1_amount: Math.abs(parseFloat(splitPart1)),
                    });
                    toast.success(`Buchung aufgeteilt: ${data.part1}€ + ${data.part2}€`);
                    setSplitting(null);
                    setSplitPart1("");
                    load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Aufteilen fehlgeschlagen");
                  }
                }}
              >
                Aufteilen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "text-slate-900" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-xl font-semibold tabular-nums mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 ${className}`}>{children}</th>;
}
function SortTh({ children, k, cur, dir, setBy, setDir, className = "" }) {
  // iter160g — Klickbare Spaltenköpfe für Sortierung
  const active = cur === k;
  const arrow = !active ? "↕" : dir === "asc" ? "↑" : "↓";
  const handle = () => {
    if (active) setDir(dir === "asc" ? "desc" : "asc");
    else { setBy(k); setDir("desc"); }
  };
  return (
    <th onClick={handle}
        data-testid={`bank-sort-th-${k}`}
        className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100 ${
          active ? "text-teal-700" : "text-slate-600"} ${className}`}>
      {children} <span className="ml-1 text-[10px] opacity-70">{arrow}</span>
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 align-middle ${className}`}>{children}</td>;
}

function ImportDialog({ onSaved }) {
  const fileRef = useRef();
  const [bankIban, setBankIban] = useState("");
  const [format, setFormat] = useState("csv");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Bitte Datei wählen"); return; }
    setLoading(true);
    const fd = new FormData();
    fd.append("file", f);
    if (bankIban) fd.append("bank_iban", bankIban);
    const endpoint = format === "camt" ? "/bank/import-camt"
                   : format === "mt940" ? "/bank/import-mt940"
                   : "/bank/import";
    try {
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const restoredMsg = data.restored ? `, ${data.restored} aus dem Archiv wiederhergestellt` : "";
      toast.success(`${data.imported} Buchungen importiert, ${data.auto_matched} automatisch zugeordnet${data.duplicates ? `, ${data.duplicates} Dubletten übersprungen` : ""}${restoredMsg}`);
      // iter132 — Kontoauszug-Assistent: Vor-Verwalter-Ausgleiche wurden durch echte Zahlungen ersetzt
      if (data.auto_settlements_replaced && data.auto_settlements_replaced > 0) {
        toast.warning(
          `${data.auto_settlements_replaced} automatisch generierte Vor-Verwalter-Ausgleiche wurden durch echte Kontoauszugsbuchungen ersetzt. Die betroffenen Mietkonten wurden aktualisiert.`,
          { duration: 9000 },
        );
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Importfehler");
    } finally {
      setLoading(false);
    }
  };

  const accept = format === "camt" ? ".xml" : format === "mt940" ? ".sta,.txt,.940" : ".csv,.txt";

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Bankimport</DialogTitle>
        <DialogDescription>
          Wählen Sie das Format Ihrer Bank-Datei. Erkennung der Mietvertragsnummer
          (<code className="font-mono">XXX-XX-XXXXX</code>) im Verwendungszweck oder IBAN-Match auf Mieter-Konto.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label className="text-xs text-slate-700">Format</Label>
          <Select value={format} onValueChange={setFormat}>
            <SelectTrigger className="mt-1" data-testid="bank-format-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="csv">CSV (Sparkasse / Postbank / VR-Bank Export)</SelectItem>
              <SelectItem value="camt">CAMT.053 (XML – SEPA-Standard)</SelectItem>
              <SelectItem value="mt940">MT940 (SWIFT – Legacy-Banken)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-700">Datei</Label>
          <Input type="file" accept={accept} ref={fileRef} className="mt-1" data-testid="bank-file-input" />
        </div>
        <div>
          <Label className="text-xs text-slate-700">Bank-IBAN (optional, eigenes Konto)</Label>
          <Input value={bankIban} onChange={(e) => setBankIban(e.target.value)} placeholder="DE..." className="mt-1 font-mono" />
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-2.5 text-xs text-slate-600">
          <strong>Auto-Zuordnung:</strong> Die Mietvertragsnummer (Format <code className="font-mono">XXX-XX-XXXXX</code>) im Verwendungszweck wird automatisch erkannt. Fallback: IBAN-Match auf Mieter-Konto. Positive Beträge mit Treffer werden direkt als „Haben"-Buchung im Mietkonto erfasst.
        </div>
        <DialogFooter>
          <Button type="submit" disabled={loading} className="bg-slate-900 hover:bg-slate-800" data-testid="bank-import-submit">
            {loading ? "Importiere..." : "Importieren"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

// iter160g — Durchsuchbare Mietverhältnis-Auswahl (Combobox)
function TenancySearchSelect({ tenancies, value, onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = React.useRef(null);
  const selected = tenancies.find((t) => t.id === value);
  const q = query.trim().toLowerCase();
  const filtered = !q ? tenancies : tenancies.filter((t) => {
    const hay = [
      t.contract_number || "",
      (t.tenant_names || []).join(" "),
      t.unit_label || "",
      t.property_name || "",
    ].join(" ").toLowerCase();
    return hay.includes(q);
  });
  return (
    <div>
      <Label className="text-xs text-slate-700">Mietverhältnis suchen</Label>
      <div className="relative mt-1">
        <input
          ref={inputRef}
          type="text"
          data-testid="assign-tenancy-search"
          className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder={selected
            ? `${selected.contract_number} · ${(selected.tenant_names || []).join(", ")}`
            : "Nach Vertragsnr., Mieter, Objekt oder Einheit suchen…"}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {open && filtered.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg z-50">
            {filtered.slice(0, 50).map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`assign-tenancy-option-${t.id}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(t.id); setQuery(""); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-0 ${
                  value === t.id ? "bg-teal-50" : ""}`}
              >
                <div className="font-medium text-slate-900">
                  {t.contract_number} · {(t.tenant_names || []).join(", ")}
                </div>
                <div className="text-slate-500 mt-0.5">
                  {t.unit_label} {t.property_name ? `· ${t.property_name}` : ""}
                </div>
              </button>
            ))}
            {filtered.length > 50 && (
              <div className="px-3 py-1.5 text-[10px] text-slate-500 italic border-t">
                {filtered.length - 50} weitere Treffer — bitte präzisieren
              </div>
            )}
          </div>
        )}
        {open && q && filtered.length === 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md p-3 text-xs text-slate-500 z-50">
            Keine Mietverhältnisse für „{q}"
          </div>
        )}
      </div>
      {selected && !query && (
        <div className="mt-1 text-[11px] text-slate-500">
          Ausgewählt: <span className="text-slate-900 font-medium">{selected.contract_number}</span> ·
          {" "}{(selected.tenant_names || []).join(", ")} · {selected.unit_label}
        </div>
      )}
    </div>
  );
}


function AssignDialog({ tx, tenancies, onClose, onSaved }) {
  const [tenancyId, setTenancyId] = useState("");
  const [category, setCategory] = useState("rent_cold");
  // iter159t — Split-Preview
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewErr, setPreviewErr] = useState(null);
  // iter159u — Manueller Modus (User erzwingt Kategorie oder splittet selbst)
  const [mode, setMode] = useState("auto"); // "auto" | "force" | "manual"
  const [manualSplits, setManualSplits] = useState([{ category: "rent_cold", amount: "" }]);
  const txAmount = Math.abs(Number(tx.amount || 0));

  useEffect(() => {
    if (!tenancyId || mode !== "auto") { setPreview(null); setPreviewErr(null); return; }
    let alive = true;
    setPreviewBusy(true); setPreviewErr(null);
    api.get(`/bank/transactions/${tx.id}/split-preview`, { params: { tenancy_id: tenancyId } })
      .then((r) => { if (alive) setPreview(r.data); })
      .catch((e) => { if (alive) setPreviewErr(e?.response?.data?.detail || "Split-Vorschau nicht möglich"); })
      .finally(() => { if (alive) setPreviewBusy(false); });
    return () => { alive = false; };
  }, [tenancyId, tx.id, mode]);

  const fmtEur = (v) => `${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const fmtMonth = (yyyyMm) => {
    if (!yyyyMm) return "—";
    const [y, m] = String(yyyyMm).split("-");
    const names = ["", "Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
    return `${names[parseInt(m, 10)] || m} ${y}`;
  };

  const splitsSum = manualSplits.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  const splitsMatch = Math.abs(splitsSum - txAmount) < 0.01;

  const addSplitLine = () => {
    const remaining = Math.max(0, txAmount - splitsSum);
    setManualSplits([...manualSplits, { category: "utilities", amount: remaining ? remaining.toFixed(2) : "" }]);
  };
  const removeSplitLine = (i) => setManualSplits(manualSplits.filter((_, idx) => idx !== i));
  const updateSplit = (i, patch) => setManualSplits(manualSplits.map((s, idx) => idx === i ? { ...s, ...patch } : s));

  const submit = async (e) => {
    e.preventDefault();
    if (!tenancyId) return;
    try {
      const payload = { tenancy_id: tenancyId, category };
      if (mode === "force") {
        payload.force_category = true;
      } else if (mode === "manual") {
        if (!splitsMatch) {
          toast.error(`Split-Summe (${fmtEur(splitsSum)}) muss dem Umsatz (${fmtEur(txAmount)}) entsprechen`);
          return;
        }
        payload.splits = manualSplits
          .filter((s) => Number(s.amount) > 0)
          .map((s) => ({ category: s.category, amount: Number(s.amount) }));
      }
      const res = await callWithMisdirect(async (extra) => (await api.post(`/bank/transactions/${tx.id}/assign`, { ...payload, ...extra })).data);
      if (!res) return;
      toast.success(res.misdirected ? "Als Fehleingang zugeordnet · Aufgabe zum Weiterleiten angelegt" : "Buchung zugeordnet");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Buchung manuell zuordnen</DialogTitle></DialogHeader>
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs space-y-1">
          <div className="text-slate-500">Datum: <span className="text-slate-900 tabular-nums">{formatDate(tx.booking_date)}</span></div>
          <div className="text-slate-500">Auftraggeber: <span className="text-slate-900">{tx.counterparty_name}</span></div>
          <div className="text-slate-500">Betrag: <span className="text-slate-900 tabular-nums font-medium">{fmtEur(txAmount)}</span></div>
          <div className="text-slate-500">Zweck: <span className="text-slate-700">{tx.purpose}</span></div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <TenancySearchSelect
            tenancies={tenancies}
            value={tenancyId}
            onChange={setTenancyId}
          />
          {tenancyId && (
            <>
              {/* Mode-Umschalter */}
              <div className="flex gap-1 border border-slate-200 rounded-md p-0.5 bg-slate-50">
                {[["auto", "Auto-Split"], ["force", "Nur eine Kategorie"], ["manual", "Manuell splitten"]].map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setMode(k)}
                    className={`flex-1 px-2 py-1 text-xs rounded ${mode === k ? "bg-white shadow-sm text-slate-900 font-medium" : "text-slate-500 hover:text-slate-700"}`}
                    data-testid={`assign-mode-${k}`}>
                    {label}
                  </button>
                ))}
              </div>

              {mode === "auto" && (
                <div>
                  {previewBusy && <div className="text-xs text-slate-500 italic">Berechne Split…</div>}
                  {previewErr && (
                    <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-800">{previewErr}</div>
                  )}
                  {preview && !previewBusy && (
                    <div className="bg-emerald-50/40 border border-emerald-200 rounded p-2.5" data-testid="assign-preview">
                      <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold mb-1.5">
                        BGB-Wasserfall · Zielmonat: {fmtMonth(preview.target_month)}
                      </div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-[10px] text-slate-500 border-b border-emerald-200">
                            <th className="py-0.5">Kategorie</th><th>Zeitraum</th><th className="text-right">Betrag</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(preview.splits || []).map((s, i) => (
                            <tr key={i} className="border-b border-emerald-100 last:border-b-0">
                              <td className="py-1 pr-2">
                                <span className="font-medium">{s.label}</span>
                                {s.reason && <div className="text-[10px] text-slate-500">{s.reason}</div>}
                              </td>
                              <td className="text-[11px] text-slate-600">{s.posting_date || "—"}</td>
                              <td className="text-right font-mono tabular-nums">{fmtEur(s.amount)}</td>
                            </tr>
                          ))}
                          <tr className="font-semibold">
                            <td className="py-1.5" colSpan="2">Gesamt</td>
                            <td className="text-right font-mono tabular-nums py-1.5">{fmtEur(preview.total)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {mode === "force" && (
                <div>
                  <Label className="text-xs text-slate-700">Kategorie (wird EXAKT so gebucht)</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="mt-1" data-testid="assign-force-cat"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(POSTING_CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-slate-500 mt-1 italic">
                    Der gesamte Betrag ({fmtEur(txAmount)}) wird auf diese Kategorie gebucht — nützlich z. B. für Kautions-Zahlungen.
                  </div>
                </div>
              )}

              {mode === "manual" && (
                <div className="space-y-2" data-testid="assign-manual-splits">
                  <Label className="text-xs text-slate-700">Manuelle Aufteilung</Label>
                  {manualSplits.map((s, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Select value={s.category} onValueChange={(v) => updateSplit(i, { category: v })}>
                        <SelectTrigger className="flex-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(POSTING_CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input type="number" step="0.01" min="0" value={s.amount}
                        onChange={(e) => updateSplit(i, { amount: e.target.value })}
                        placeholder="0.00" className="w-28 h-8 text-right text-xs tabular-nums" />
                      <button type="button" onClick={() => removeSplitLine(i)}
                        className="text-slate-400 hover:text-rose-600 text-xs px-1"
                        disabled={manualSplits.length === 1}>×</button>
                    </div>
                  ))}
                  <div className="flex items-center justify-between text-xs">
                    <button type="button" onClick={addSplitLine}
                      className="text-emerald-700 hover:underline"
                      data-testid="assign-add-split">+ Zeile hinzufügen</button>
                    <div className={splitsMatch ? "text-emerald-700" : "text-rose-600"}>
                      Summe: {fmtEur(splitsSum)} / {fmtEur(txAmount)} {splitsMatch ? "✓" : "✗"}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <DialogFooter>
            <Button type="submit" disabled={!tenancyId || previewBusy || (mode === "manual" && !splitsMatch)}
              className="bg-slate-900 hover:bg-slate-800" data-testid="assign-save">
              Zuordnen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ManualEntryDialog({ onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    booking_date: today,
    valuta_date: "",
    amount: "",
    direction: "in", // 'in' = Eingang (positiv), 'out' = Ausgang (negativ)
    counterparty_name: "",
    counterparty_iban: "",
    purpose: "",
    bank_account_iban: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Wahre Validierung erfolgt im Backend; hier nur eine Live-Hinweisbox.
  const issues = [];
  if (!form.booking_date) issues.push("Buchungsdatum fehlt");
  const amt = parseFloat(String(form.amount).replace(",", "."));
  if (!form.amount || isNaN(amt) || amt === 0) issues.push("Betrag ist Pflicht und ≠ 0");
  if (!form.counterparty_name.trim() && !form.purpose.trim())
    issues.push("Auftraggeber ODER Verwendungszweck angeben");
  if (form.counterparty_iban &&
      !/^[A-Z]{2}\d{2}[A-Z0-9]{4,30}$/i.test(form.counterparty_iban.replace(/\s+/g, "")))
    issues.push("IBAN-Format unplausibel");

  const submit = async (e) => {
    e.preventDefault();
    if (issues.length > 0) {
      toast.error(issues[0]);
      return;
    }
    setSubmitting(true);
    try {
      const signed = form.direction === "out" ? -Math.abs(amt) : Math.abs(amt);
      const payload = {
        booking_date: form.booking_date,
        valuta_date: form.valuta_date || null,
        amount: signed,
        counterparty_name: form.counterparty_name.trim() || null,
        counterparty_iban: form.counterparty_iban.replace(/\s+/g, "").toUpperCase() || null,
        purpose: form.purpose.trim() || null,
        bank_account_iban: form.bank_account_iban.replace(/\s+/g, "").toUpperCase() || null,
      };
      const { data } = await api.post("/bank/transactions/manual", payload);
      if (data.auto_matched > 0)
        toast.success("Buchung erfasst und automatisch zugeordnet");
      else
        toast.success("Buchung erfasst – bitte ggf. manuell zuordnen");
      if (data.auto_settlements_replaced && data.auto_settlements_replaced > 0) {
        toast.warning(
          `${data.auto_settlements_replaced} automatisch generierte Vor-Verwalter-Ausgleiche wurden durch diese echte Buchung ersetzt.`,
          { duration: 8000 },
        );
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Buchung konnte nicht erfasst werden");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="max-w-lg" data-testid="bank-manual-dialog">
      <DialogHeader>
        <DialogTitle>Bankbuchung manuell erfassen</DialogTitle>
        <DialogDescription>
          Erfassen Sie eine einzelne Buchung — z. B. eine Bareinzahlung oder eine
          Überweisung, die noch nicht im Bankexport enthalten ist. Die automatische
          Mietvertrags-Zuordnung greift wie beim CSV-Import.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-700">Buchungsdatum *</Label>
            <Input type="date" value={form.booking_date}
                   onChange={(e) => set("booking_date", e.target.value)}
                   className="mt-1" data-testid="manual-booking-date" />
          </div>
          <div>
            <Label className="text-xs text-slate-700">Valutadatum (optional)</Label>
            <Input type="date" value={form.valuta_date}
                   onChange={(e) => set("valuta_date", e.target.value)}
                   className="mt-1" data-testid="manual-valuta-date" />
          </div>
        </div>

        <div className="grid grid-cols-[1fr_140px] gap-3">
          <div>
            <Label className="text-xs text-slate-700">Betrag (€) *</Label>
            <Input type="text" inputMode="decimal"
                   value={form.amount}
                   onChange={(e) => set("amount", e.target.value)}
                   placeholder="z. B. 1180,00"
                   className="mt-1 tabular-nums" data-testid="manual-amount" />
          </div>
          <div>
            <Label className="text-xs text-slate-700">Richtung</Label>
            <Select value={form.direction} onValueChange={(v) => set("direction", v)}>
              <SelectTrigger className="mt-1" data-testid="manual-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="in">Eingang (+)</SelectItem>
                <SelectItem value="out">Ausgang (−)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-xs text-slate-700">Auftraggeber</Label>
          <Input value={form.counterparty_name}
                 onChange={(e) => set("counterparty_name", e.target.value)}
                 placeholder="z. B. Anna Schmidt"
                 className="mt-1" data-testid="manual-counterparty-name" />
        </div>
        <div>
          <Label className="text-xs text-slate-700">IBAN Auftraggeber (optional)</Label>
          <Input value={form.counterparty_iban}
                 onChange={(e) => set("counterparty_iban", e.target.value)}
                 placeholder="DE89 3704 0044 0532 0130 00"
                 className="mt-1 font-mono" data-testid="manual-counterparty-iban" />
        </div>
        <div>
          <Label className="text-xs text-slate-700">Verwendungszweck</Label>
          <Input value={form.purpose}
                 onChange={(e) => set("purpose", e.target.value)}
                 placeholder="z. B. Miete März 2026 Vertrag 001-01-00001"
                 className="mt-1" data-testid="manual-purpose" />
          <p className="text-[10px] text-slate-500 mt-1">
            Tipp: Wenn die Mietvertragsnummer (Format <code className="font-mono">XXX-XX-XXXXX</code>) im Verwendungszweck steht, ordnet das System die Buchung automatisch zu.
          </p>
        </div>

        {issues.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-sm p-2 text-xs"
               data-testid="manual-issues">
            <strong>Bitte ergänzen:</strong>
            <ul className="list-disc list-inside mt-1">
              {issues.map((i) => <li key={i}>{i}</li>)}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button type="submit" disabled={submitting || issues.length > 0}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="manual-submit">
            {submitting ? "Speichere …" : "Buchung erfassen"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}



function LearnRulesCard() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const load = () => {
    setLoading(true);
    api.get("/bank/learn-rules")
      .then((r) => setRules(r.data?.rules || []))
      .catch(() => setRules([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!await window._domivusConfirm("Diese gelernte Zuordnung wirklich löschen?")) return;
    try {
      await api.delete(`/bank/learn-rules/${id}`);
      toast.success("Regel gelöscht");
      load();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };
  if (!loading && rules.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="learn-rules-card">
      <button type="button" className="w-full text-left flex items-center justify-between"
              onClick={() => setExpanded((v) => !v)} data-testid="learn-rules-toggle">
        <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
          🧠 Gelernte Zuordnungen
          <span className="text-[11px] text-slate-500 font-normal">({rules.length} Regel{rules.length === 1 ? "" : "n"})</span>
        </div>
        <span className="text-slate-400 text-xs">{expanded ? "▴ ausblenden" : "▾ anzeigen"}</span>
      </button>
      {expanded && (
        <div className="mt-3 border border-slate-100 rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-2 py-1.5">Auftraggeber</th>
                <th className="text-left px-2 py-1.5">IBAN</th>
                <th className="text-left px-2 py-1.5">Zugeordnet zu MV</th>
                <th className="text-left px-2 py-1.5">Zuletzt genutzt</th>
                <th className="text-right px-2 py-1.5 w-16">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-2 py-1.5">{r.counterparty_name_display || r.counterparty_name_norm || "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px] tabular-nums">{r.counterparty_iban || "—"}</td>
                  <td className="px-2 py-1.5">
                    <span className="font-mono text-[11px]">{r.contract_number || "—"}</span>
                    {r.tenant_names?.length > 0 && <div className="text-[10px] text-slate-500">{r.tenant_names.join(", ")}</div>}
                  </td>
                  <td className="px-2 py-1.5 text-[11px] text-slate-600">{formatDate(r.updated_at?.slice(0, 10))}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button onClick={() => del(r.id)} className="text-rose-600 hover:text-rose-700 text-[11px]"
                            data-testid={`learn-rule-delete-${r.id}`}>Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
